import { useState, useMemo } from 'react'
import { Search, Check, ChevronDown, ChevronRight, Minus, Plus } from 'lucide-react'
import { PageSpinner } from '@/components/ui/Spinner'
import { useMenuItems, useMenuCategories } from '@/hooks/useMenu'
import { useProductStock, useAddProductStock } from '@/hooks/useProductStock'
import type { MenuItem } from '@/types/app'

function StockCard({
  item,
  onHand,
}: {
  item: MenuItem
  onHand: number | undefined
}) {
  const { mutateAsync, isPending } = useAddProductStock()
  const [value, setValue]   = useState('')
  const [saved, setSaved]   = useState(false)

  const current  = onHand ?? 0
  const inputNum = value === '' ? null : parseInt(value, 10)
  const hasChange = inputNum !== null && !isNaN(inputNum) && inputNum !== current

  function adjust(delta: number) {
    const base = inputNum !== null && !isNaN(inputNum) ? inputNum : current
    setValue(String(Math.max(0, base + delta)))
    setSaved(false)
  }

  async function handleSet() {
    if (!hasChange || inputNum === null) return
    const diff = inputNum - current
    await mutateAsync({
      menuItemId:    item.id,
      txnType:       diff > 0 ? 'in' : 'out',
      qty:           Math.abs(diff),
      referenceType: 'adjustment',
    })
    setValue('')
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const stockColor =
    onHand === undefined ? 'text-stone-300'
    : onHand <= 0        ? 'text-danger font-bold'
    : onHand <= 5        ? 'text-warning-dark font-bold'
    : 'text-success-dark font-bold'

  return (
    <div className={[
      'rounded-2xl border px-4 py-3 transition-all duration-200',
      saved ? 'border-green-300 bg-green-50' : 'border-stone-200 bg-white',
    ].join(' ')}>

      {/* Row 1: image + name + current count */}
      <div className="flex items-center gap-2.5 mb-3">
        {/* Thumbnail */}
        <div className="w-9 h-9 rounded-lg overflow-hidden bg-brand-100 flex items-center justify-center shrink-0">
          {item.images?.[0]
            ? <img src={item.images[0]} alt="" className="w-full h-full object-cover" />
            : <span className="text-sm font-bold text-brand-700">{item.name[0]?.toUpperCase()}</span>
          }
        </div>

        {/* Name */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-stone-900 leading-snug">{item.name}</p>
          {item.regional_name && (
            <p className="text-2xs text-stone-400">{item.regional_name}</p>
          )}
        </div>

        {/* Current stock */}
        <div className="text-right shrink-0">
          <span className={['text-lg leading-none', stockColor].join(' ')}>
            {onHand ?? '—'}
          </span>
          <p className="text-2xs text-stone-400">on hand</p>
        </div>
      </div>

      {/* Row 2: − [input] + [Set] — fixed widths so Set is always visible */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => adjust(-1)}
          className="h-10 w-10 rounded-xl bg-stone-100 active:bg-stone-200 text-stone-600 flex items-center justify-center transition-colors shrink-0"
        >
          <Minus size={16} />
        </button>

        <input
          type="number"
          inputMode="numeric"
          min="0"
          max="999"
          placeholder={String(current)}
          value={value}
          onChange={e => { setValue(e.target.value); setSaved(false) }}
          onKeyDown={e => e.key === 'Enter' && handleSet()}
          className={[
            'w-16 h-10 text-center text-base font-semibold border-2 rounded-xl focus:outline-none transition-colors shrink-0',
            hasChange
              ? 'border-brand-400 bg-brand-50 text-brand-700'
              : 'border-stone-200 bg-stone-50 text-stone-500',
          ].join(' ')}
        />

        <button
          onClick={() => adjust(1)}
          className="h-10 w-10 rounded-xl bg-stone-100 active:bg-stone-200 text-stone-600 flex items-center justify-center transition-colors shrink-0"
        >
          <Plus size={16} />
        </button>

        {/* Spacer pushes Set to the right */}
        <div className="flex-1" />

        <button
          onClick={handleSet}
          disabled={!hasChange || isPending}
          className={[
            'h-10 w-16 rounded-xl text-sm font-semibold transition-colors shrink-0 flex items-center justify-center',
            saved
              ? 'bg-green-500 text-white'
              : hasChange
                ? 'bg-brand-600 text-white active:bg-brand-700'
                : 'bg-stone-100 text-stone-300 cursor-not-allowed',
          ].join(' ')}
        >
          {saved ? <Check size={15} /> : isPending ? '…' : 'Set'}
        </button>
      </div>
    </div>
  )
}

function CategorySection({
  name,
  items,
  stockMap,
  defaultOpen,
}: {
  name: string
  items: MenuItem[]
  stockMap: Map<string, { on_hand: number }> | undefined
  defaultOpen: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)

  const zeroCount = items.filter(i => { const s = stockMap?.get(i.id); return s !== undefined && s.on_hand <= 0 }).length
  const lowCount  = items.filter(i => { const s = stockMap?.get(i.id); return s !== undefined && s.on_hand > 0 && s.on_hand <= 5 }).length

  return (
    <div>
      {/* Category header */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-2 px-1 text-left mb-2"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-widest text-stone-500">{name}</span>
          <span className="text-xs text-stone-400">({items.length})</span>
          {zeroCount > 0 && (
            <span className="text-2xs font-semibold text-danger bg-red-50 px-1.5 py-0.5 rounded-full">
              {zeroCount} out
            </span>
          )}
          {zeroCount === 0 && lowCount > 0 && (
            <span className="text-2xs font-semibold text-warning-dark bg-amber-50 px-1.5 py-0.5 rounded-full">
              {lowCount} low
            </span>
          )}
        </div>
        {open
          ? <ChevronDown size={14} className="text-stone-400" />
          : <ChevronRight size={14} className="text-stone-400" />
        }
      </button>

      {open && (
        <div className="space-y-2 mb-4">
          {items.map(item => (
            <StockCard
              key={item.id}
              item={item}
              onHand={stockMap?.get(item.id)?.on_hand}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function StockUpdatePage() {
  const { data: allItems = [],   isLoading: itemsLoading } = useMenuItems()
  const { data: categories = [], isLoading: catsLoading }  = useMenuCategories()
  const { data: stockMap,        isLoading: stockLoading } = useProductStock()
  const [search, setSearch] = useState('')

  const q = search.toLowerCase()
  const filtered = useMemo(() => {
    if (!q) return allItems
    return allItems.filter(i =>
      i.name.toLowerCase().includes(q) ||
      (i.regional_name ?? '').toLowerCase().includes(q)
    )
  }, [allItems, q])

  const sortedCats = useMemo(
    () => [...categories].sort((a, b) => a.display_order - b.display_order),
    [categories]
  )

  const totalOut = stockMap
    ? allItems.filter(i => (stockMap.get(i.id)?.on_hand ?? 1) <= 0).length
    : 0
  const totalLow = stockMap
    ? allItems.filter(i => { const s = stockMap.get(i.id); return s && s.on_hand > 0 && s.on_hand <= 5 }).length
    : 0

  if (itemsLoading || catsLoading || stockLoading) return <PageSpinner />

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-lg mx-auto pb-20">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-xl font-bold text-stone-900">Stock Update</h1>
          <div className="flex items-center gap-2 shrink-0">
            {totalOut > 0 && (
              <span className="text-xs font-semibold text-danger bg-red-50 border border-red-200 px-2 py-1 rounded-lg">
                {totalOut} out
              </span>
            )}
            {totalLow > 0 && (
              <span className="text-xs font-semibold text-warning-dark bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg">
                {totalLow} low
              </span>
            )}
          </div>
        </div>
        <p className="text-sm text-stone-400 mt-0.5">Tap +/− or type a new count, then Set.</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Search product…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-stone-300 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white"
        />
      </div>

      {/* Cards */}
      {q ? (
        <div className="space-y-2">
          {filtered.length === 0
            ? <p className="text-center text-stone-400 py-12 text-sm">No products found</p>
            : filtered.map(item => (
                <StockCard key={item.id} item={item} onHand={stockMap?.get(item.id)?.on_hand} />
              ))
          }
        </div>
      ) : (
        <div>
          {sortedCats.map((cat, idx) => {
            const catItems = filtered.filter(i => i.category_id === cat.id)
            if (catItems.length === 0) return null
            return (
              <CategorySection
                key={cat.id}
                name={cat.name}
                items={catItems}
                stockMap={stockMap}
                defaultOpen={idx === 0}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
